import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Panel from '../Panel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
    Curve,
    Point,
    VictoryAxis,
    VictoryChart,
    VictoryLabel,
    VictoryLegend,
    VictoryLine,
    VictoryScatter
} from 'victory';
import { useTooltip } from '../../hooks/tooltip';
import {
    calculateScoresYDomain,
    convertEbabPeriod,
    generateEbabPeriods
} from '../../functions/charts';
import { ScoreOverTimeTooltip } from '../tooltips/ScoreOverTimeTooltip';

/* Y-axis padding either side of minimum and maximum score */
const Y_AXIS_PADDING = 10;

/* Styling for X and Y axis with responsive font size */
const axisStyle = (theme, isBelowMd) => ({
    axis: {
        strokeWidth: 0
    },
    grid: {
        stroke: theme.palette.overTimeChart.gridStroke,
        strokeWidth: 0.5,
        strokeDasharray: '2'
    },
    tickLabels: {
        fontFamily: theme.typography.fontFamily,
        fontSize: isBelowMd ? 20 : 14
    }
});

/**
 * Returns the active (hovered-over) chart dot style,
 * with a larger size and updated color.
 * @param {Object} props - props of the chart element being mutated
 * @param {String} activeColor - color to apply to element
 * @returns {*&{size: number, style: (*&{fill})}} - new element props
 */
function getActiveChartStyle(props, activeColor) {
    return {
        ...props,
        style: {
            ...props.style,
            fill: activeColor
        },
        size: 8
    };
}

/**
 * Function used to return the inactive dot and line styles,
 * used in the chart to grey-out non-selected chart elements.
 * @param {Object} props - props of the chart element being mutated
 * @param {Object} theme - App theme
 * @returns {{style: {fill: string}}|{style: (*&{stroke: (string)})}} - new element style
 */
function getInactiveChartStyle(props, theme) {
    switch (props.style.fill) {
        case theme.palette.kimberley.darker:
            return {
                style: {
                    fill: theme.palette.kimberley.lighter
                }
            };
        case theme.palette.overTimeChart.median:
            return {
                style: {
                    fill: theme.palette.overTimeChart.medianGreyed
                }
            };
        case 'transparent':
            return {
                style: {
                    ...props.style,
                    stroke:
                        props.style.stroke === theme.palette.kimberley.darker
                            ? theme.palette.kimberley.lighter
                            : theme.palette.overTimeChart.medianGreyed
                }
            };
    }
}

/**
 * Function to calculate absolute coordinates of hovered dot's tooltip for the BA score over time chart.
 * Uses tooltip sizing defined in theme.js and accounts for page overlap.
 *
 * @param {Object} tooltipSizing - tooltip sizing from theme.js
 * @param {Object} targetDot - hovered chart dot
 * @returns {{yPos: number, xPos: number}} -
 */
function calculateTooltipCoordinates(tooltipSizing, targetDot) {
    const dotCoords = targetDot.getBoundingClientRect();

    let xPos = dotCoords.left - tooltipSizing.width - tooltipSizing.offsetX;
    const yPos = dotCoords.top + window.scrollY - tooltipSizing.offsetY;

    //prevent tooltip leaving left side of screen
    if (xPos < tooltipSizing.offsetX) {
        xPos += tooltipSizing.width + tooltipSizing.offsetX * 2 + 8;
    }

    return { xPos, yPos };
}

/**
 * Renders a responsive Behavioral Alpha Score line chart used for the Pro dashboard.
 * Shows the currently selected portfolio's 2-3 year rolling BA score alongside the median score for that peer group,
 * up to the current EBAB period. Allows the user to hover over each dot to see more information.
 * The Y-axis auto-scales to fit the data.
 *
 * @component
 * @param {Array<Object>} overTimeData - currently selected portfolio's over time data
 * @param {String} portfolioName - currently selected portfolio long name
 * @param {String} currentPeriod - current EBAB period
 * @returns {React.ReactElement}
 */
const ProBaScoreOverTime = ({ overTimeData, portfolioName, currentPeriod }) => {
    const theme = useTheme();

    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const { tooltipState, setTooltipState, clearTooltip } = useTooltip();

    const quartersToPlot = isBelowMd ? 8 : 12;
    let yDomain = [0, 100];

    const ebabPeriodsArray = generateEbabPeriods(
        currentPeriod || '2024-Q4',
        quartersToPlot
    );
    const dataToPlot = overTimeData?.filter((item) =>
        ebabPeriodsArray.includes(item.ebabPeriod)
    );

    if (dataToPlot && dataToPlot.length > 0) {
        yDomain = calculateScoresYDomain(dataToPlot, Y_AXIS_PADDING);
    }

    return (
        <>
            <Panel
                sx={{
                    flexDirection: 'column',
                    width: '100%',
                    paddingTop: '2.5rem',
                    paddingLeft: isBelowMd ? '2rem' : '3rem',
                    paddingRight: isBelowMd ? '2rem' : '3rem'
                }}
                data-analyticsid='portfolio-drilldown-ba-score-ot'
            >
                <Typography variant='h2'>
                    Behavioral Alpha® Score Over Time
                </Typography>
                <Typography variant='body1' color={theme.palette.font.light}>
                    Rolling {isBelowMd ? 2 : 3} year period
                </Typography>
                <VictoryChart
                    height={270}
                    width={1080}
                    domain={{ y: yDomain }}
                    domainPadding={{ x: isBelowMd ? [25, 53] : [10, 38], y: 5 }}
                    padding={{ top: 10, bottom: 35, left: 33, right: 5 }}
                    events={[
                        {
                            childName: ['baScoreScatter', 'medianScatter'],
                            target: 'data',
                            eventHandlers: {
                                onMouseOver: (event, data) => {
                                    const coordinates =
                                        calculateTooltipCoordinates(
                                            theme.sizing.tooltip.overTime,
                                            event.target
                                        );

                                    setTooltipState({
                                        hovered: true,
                                        posX: coordinates.xPos,
                                        posY: coordinates.yPos,
                                        selectedDot: data.datum
                                    });
                                    return [
                                        {
                                            childName: [
                                                'baScoreScatter',
                                                'medianScatter',
                                                'baScoreLine',
                                                'medianLine'
                                            ],
                                            target: 'data',
                                            eventKey: 'all',
                                            mutation: (props) =>
                                                getInactiveChartStyle(
                                                    props,
                                                    theme
                                                )
                                        },
                                        {
                                            childName: ['baScoreScatter'],
                                            target: 'data',
                                            mutation: (props) =>
                                                getActiveChartStyle(
                                                    props,
                                                    theme.palette.kimberley
                                                        .darkest
                                                )
                                        },
                                        {
                                            childName: ['medianScatter'],
                                            target: 'data',
                                            mutation: (props) =>
                                                getActiveChartStyle(
                                                    props,
                                                    theme.palette.overTimeChart
                                                        .median
                                                )
                                        }
                                    ];
                                },
                                onMouseOut: () => {
                                    clearTooltip();
                                    return [
                                        {
                                            childName: 'all',
                                            target: 'data',
                                            eventKey: 'all',
                                            mutation: () => ({})
                                        }
                                    ];
                                }
                            }
                        }
                    ]}
                >
                    <VictoryAxis
                        dependentAxis
                        style={axisStyle(theme, isBelowMd)}
                        tickLabelComponent={
                            <VictoryLabel textAnchor={'start'} dx={-20} />
                        }
                    />
                    <VictoryAxis
                        tickValues={ebabPeriodsArray}
                        tickFormat={(t) => convertEbabPeriod(t)}
                        style={axisStyle(theme, isBelowMd)}
                    />
                    <VictoryLine
                        name='medianLine'
                        dataComponent={<Curve role='medianLine' />}
                        data={dataToPlot}
                        x='ebabPeriod'
                        y={['peerGroupMetrics', 'medianBaScore']}
                        categories={{ x: ebabPeriodsArray }}
                        style={{
                            data: {
                                stroke: theme.palette.overTimeChart.median,
                                strokeWidth: 1.5,
                                strokeDasharray: '3'
                            }
                        }}
                    />
                    <VictoryScatter
                        name='medianScatter'
                        dataComponent={
                            <Point
                                role='medianDot'
                                data-analyticsid='peer-ba-score-dot'
                            />
                        }
                        data={dataToPlot}
                        categories={{ x: ebabPeriodsArray }}
                        x='ebabPeriod'
                        y={['peerGroupMetrics', 'medianBaScore']}
                        style={{
                            data: {
                                fill: theme.palette.overTimeChart.median
                            }
                        }}
                        size={5}
                    />
                    <VictoryLine
                        name='baScoreLine'
                        dataComponent={<Curve role='baScoreLine' />}
                        data={dataToPlot}
                        x='ebabPeriod'
                        y='baScore'
                        categories={{ x: ebabPeriodsArray }}
                        style={{
                            data: {
                                stroke: theme.palette.kimberley.darker,
                                strokeWidth: 1.5
                            }
                        }}
                    />
                    <VictoryScatter
                        name='baScoreScatter'
                        dataComponent={
                            <Point
                                role='baScoreDot'
                                data-analyticsid='portfolio-ba-score-dot'
                            />
                        }
                        data={dataToPlot}
                        categories={{ x: ebabPeriodsArray }}
                        x='ebabPeriod'
                        y='baScore'
                        style={{
                            data: {
                                fill: theme.palette.kimberley.darker
                            }
                        }}
                        size={5}
                    />
                </VictoryChart>
                {/*Chart Key*/}
                {dataToPlot && dataToPlot.length > 0 && (
                    <VictoryLegend
                        orientation={'horizontal'}
                        symbolSpacer={isBelowMd ? 15 : 10}
                        gutter={30}
                        height={isBelowMd ? 50 : 30}
                        width={1080}
                        x={40}
                        y={isBelowMd ? 20 : 10}
                        data={
                            portfolioName
                                ? [
                                      {
                                          name: portfolioName,
                                          symbol: {
                                              fill: theme.palette.kimberley
                                                  .darker
                                          }
                                      },
                                      {
                                          name: 'Median Peer Group Score',
                                          symbol: {
                                              fill: theme.palette.overTimeChart
                                                  .median
                                          }
                                      }
                                  ]
                                : []
                        }
                        style={{
                            labels: {
                                fontSize: isBelowMd ? 20 : 15,
                                fontFamily: theme.typography.fontFamily,
                                fontWeight: 300,
                                fill: theme.palette.font.light
                            }
                        }}
                    />
                )}
            </Panel>
            {(tooltipState.hovered || tooltipState.selected) && (
                <ScoreOverTimeTooltip
                    position={{
                        x: tooltipState.posX,
                        y: tooltipState.posY
                    }}
                    scoreData={tooltipState.selectedDot}
                />
            )}
        </>
    );
};

ProBaScoreOverTime.propTypes = {
    overTimeData: PropTypes.array,
    portfolioName: PropTypes.string,
    currentPeriod: PropTypes.string
};
export default ProBaScoreOverTime;
